import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { useCommon } from 'src/domains/common/Common';
import { Messages } from 'src/domains/layouts/webview/components/Messages/Messages';
import { DepositLimitReadonlyCard, TopUpLimitsProcedureItem } from './RollingNetDepositLimitItem';
import {
    RollingDepositLimitForm,
    ButtonSubmit,
    TopUpLimitsInput,
    ReadonlyCardsStack,
    FormFieldBlock,
    EditFormActions,
    ReadonlyIntro,
    FullWidthSecondaryButton,
    HeaderTitle,
    SectionDescritpion,
    DepositLimitHelperMailLink,
    DepositLimitErrorMessage,
    LimitsTypeSection,
} from 'src/domains/players/webview/components/Account/limitsTab/rollingNetDepositLimit/DepositLimitsProcedure.style';
import {
    RollingNetDepositLimitState,
    InfoMessageType,
} from 'src/domains/players/webview/components/Account/limitsTab/rollingNetDepositLimit/DepositLimitsProcedure.state';
import { UsersState } from 'src/domains/players/state/UsersState';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { Select } from 'src/domains/players/webview/components/form/select/Select';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { BlockingEditLimitsTitle } from '../../Notifications/BlockingNotifications.style';
import { DateTime } from 'src_common/utils/time/time';
import { Common } from 'src/domains/common/Common';
import { CurrencyType } from 'src_common/common/amount/website-money/currency';
import { PendingNewLimitSuccessContextType } from 'src/domains/players/webview/components/Account/limitsTab/rollingNetDepositLimit/DepositLimitsProcedure.state';
import { EditLimitsDescription } from 'src/domains/players/webview/components/Account/limitsTab/rollingNetDepositLimit/DepositLimitsCopy';
import { Tooltip } from 'src/domains/players/webview/components/Account/summaryTab/accountSummary/Tooltip/Tooltip';
import {
    CopyTerm,
    InlineTermWithTooltip,
} from 'src/domains/players/webview/components/Account/limitsTab/rollingNetDepositLimit/DepositLimitsCopy.style';
import { LanguageTokenType } from 'src/domains/layouts/state/languagesState/LanguagesUtils';
import { DepositLimitsCalculationToggle } from 'src/domains/players/webview/components/Account/limitsTab/rollingNetDepositLimit/DepositLimitsCalculationToggle';
import { getRollingLimits } from 'src_server/trpc/types/responsibleGambling';

export type RenderLimitApiMessageOptionsType = {
    configComponents: ConfigComponents;
    common: Common;
    pendingNewLimitContext: PendingNewLimitSuccessContextType | null;
};

export const buildPendingNewLimitMessage = (
    configComponents: ConfigComponents,
    currency: CurrencyType,
    { pendingLimitValue, pendingRequestedAt }: PendingNewLimitSuccessContextType
): JSX.Element | undefined => {
    const until = DateTime.from(pendingRequestedAt)?.addDays(1).format('HH:mm DD-MM-YYYY');

    if (pendingLimitValue === 0 || until === undefined) {
        return undefined;
    }

    const next = configComponents.precision.newFromAnything(pendingLimitValue).format(currency);

    return (
        <I18n
            langKey='account.rolling-net-deposit-limits.pending-new-limit'
            defaultText='New limit of {next} will be available at {until}'
            params={{ next, until }}
        />
    );
};

const GrossToNetNotAllowedMessage = observer('GrossToNetNotAllowedMessage', (): JSX.Element => {
    const common = useCommon();
    const languagesState = LanguagesState.get(common);
    const accountHelperMail = ConfigComponents.get(common).config.accountHelperMail;
    const text = languagesState.getTranslation(
        'account.deposit-limits-edit.error-gross-to-net',
        'Change from Deposit Limit to Net Deposit Limit is not allowed. In case of any questions please contact us [email].'
    );

    return (
        <Messages
            type='error'
            marginBottom='8px'
            marginTop='14px'
            message={
                <DepositLimitErrorMessage>
                    {languagesState.translateTokens(text, (token: LanguageTokenType): React.ReactNode => {
                        if (token.tag === 'email') {
                            return (
                                <DepositLimitHelperMailLink href={`mailto:${accountHelperMail}`}>
                                    {accountHelperMail}
                                </DepositLimitHelperMailLink>
                            );
                        }

                        return null;
                    })}
                </DepositLimitErrorMessage>
            }
            dataTest='deposit-limits-gross-to-net-error'
        />
    );
});

export const renderLimitApiMessage = (
    infoMessage: InfoMessageType | null,
    options?: RenderLimitApiMessageOptionsType
): JSX.Element | string => {
    if (infoMessage === 'error') {
        return (
            <Messages
                type='error'
                marginBottom='8px'
                message={
                    <I18n
                        langKey='account.rolling-net-deposit-limits-error-message'
                        defaultText='Sorry, we seem to have a problem. Please try again later.'
                    />
                }
            />
        );
    } else if (infoMessage === 'success') {
        const pendingMessage =
            options?.pendingNewLimitContext === null || options?.pendingNewLimitContext === undefined
                ? undefined
                : buildPendingNewLimitMessage(
                      options.configComponents,
                      BasicDataModel.get(options.common).currency,
                      options.pendingNewLimitContext
                  );

        return (
            <>
                <Messages
                    type='success'
                    marginBottom='8px'
                    marginTop='8px'
                    message={
                        <I18n
                            langKey='account.rolling-net-deposit-limits.success-message'
                            defaultText='Set limits finished successfully.'
                        />
                    }
                />
                {pendingMessage === undefined ? null : (
                    <Messages
                        type='success'
                        marginBottom='8px'
                        message={pendingMessage}
                    />
                )}
            </>
        );
    } else if (infoMessage === 'empty') {
        return (
            <Messages
                type='error'
                marginBottom='8px'
                message={
                    <I18n
                        langKey='account.deposit-limits-edit.error-empty'
                        defaultText='Please enter an amount for your deposit limit.'
                    />
                }
            />
        );
    } else if (infoMessage === 'belowOne') {
        return (
            <Messages
                type='error'
                marginBottom='8px'
                message={
                    <I18n
                        langKey='account.rolling-net-deposit-limits-error-message-below-one-deposit'
                        defaultText='You cannot set deposit limit below 1'
                    />
                }
            />
        );
    } else if (infoMessage === 'grossToNetNotAllowed') {
        return <GrossToNetNotAllowedMessage />;
    } else if (infoMessage === 'amountTypeMismatch') {
        return (
            <Messages
                type='error'
                marginBottom='8px'
                marginTop='14px'
                message={
                    <I18n
                        langKey='account.rolling-net-deposit-limits-error-message-amount-and-type'
                        defaultText='You have to choose a limit amount and type.'
                    />
                }
                dataTest='deposit-limits-amount-type-error'
            />
        );
    }

    return '';
};

interface DepositLimitsEditFormPropsType {
    state: RollingNetDepositLimitState;
    onCancel: () => void;
    variant?: 'account' | 'blocking';
    showCalculationTypeToggle?: boolean;
    showIntro?: boolean;
    showActions?: boolean;
    submitEdit?: () => Promise<void>;
}

export const DepositLimitsEditForm = observer('DepositLimitsEditForm', (props: DepositLimitsEditFormPropsType) => {
    const {
        state,
        onCancel,
        variant = 'account',
        showCalculationTypeToggle = true,
        showIntro = true,
        showActions = true,
        submitEdit,
    } = props;
    const common = useCommon();
    const configComponents = ConfigComponents.get(common);

    return (
        <RollingDepositLimitForm
            onSubmit={(e): Promise<void> => {
                if (submitEdit !== undefined) {
                    e.preventDefault();
                    return submitEdit();
                }
                return state.onSubmitEdit(e);
            }}
            variant={variant}
            data-test={
                variant === 'blocking' ? 'blocking-edit-limits-form' : 'rolling-net-deposit-limits-procedure-edit'
            }
        >
            {variant === 'blocking' ? <div /> : null}
            <div>
                {showIntro === false ? null : variant === 'account' ? (
                    <EditLimitsDescription dataTest='deposit-limits-edit-instructions' />
                ) : (
                    <>
                        <BlockingEditLimitsTitle data-test='blocking-edit-limits-title'>
                            <I18n
                                langKey='account.deposit-limits-edit.blocking.edit.title'
                                defaultText='Edit Limits'
                            />
                        </BlockingEditLimitsTitle>
                        <EditLimitsDescription dataTest='deposit-limits-edit-instructions' />
                    </>
                )}
                {showCalculationTypeToggle ? (
                    <DepositLimitsCalculationToggle
                        value={state.calculationType ?? 'gross'}
                        onChange={state.selectCalculationType}
                    />
                ) : null}
                <FormFieldBlock isSignup={false}>
                    <Select
                        labelText={
                            <I18n
                                langKey='account.deposit-limits-edit.period.label'
                                defaultText='Period'
                            />
                        }
                        options={state.periodSelectOptions}
                        state={state.periodSelectState}
                        colorTheme='light'
                        dataTest='deposit-limits-period-select'
                        onChange={(): void => {
                            state.onPeriodSelectCommitted();
                        }}
                    />
                </FormFieldBlock>
                <TopUpLimitsInput
                    type='text'
                    maxLength={10}
                    label={
                        <I18n
                            langKey='account.deposit-limits-edit.amount.label'
                            defaultText='Amount'
                        />
                    }
                    currency={BasicDataModel.get(common).moneySymbol}
                    placeholder={state.getItemForWindow(state.selectedEditWindow).activeLimit}
                    state={state.editLimitAmount}
                    onChange={state.onEditLimitChange}
                    colorTheme={variant === 'blocking' ? 'dark' : 'light'}
                    onBlur={state.onEditLimitBlur}
                    dataTest='deposit-limits-edit-amount'
                />
                {state.editAmountExceedsMax === true ? (
                    <Messages
                        type='error'
                        marginBottom='8px'
                        message={
                            <I18n
                                langKey='account.rolling-net-deposit-limits.max-limit-input'
                                defaultText='Please set limit below {maxLimitValue}'
                                params={{
                                    maxLimitValue: state.editMaxLimitFormatted,
                                }}
                            />
                        }
                        dataTest='deposit-limits-edit-max-error'
                    />
                ) : null}
                {renderLimitApiMessage(state.infoMessage, {
                    configComponents,
                    common,
                    pendingNewLimitContext: state.pendingNewLimitSuccessContext,
                })}
            </div>
            {showActions === false ? null : (
                <EditFormActions>
                    <ButtonSubmit
                        type='submit'
                        size='medium'
                        disabled={state.isDisabledSubmit}
                        isLoading={variant === 'blocking' && state.isSubmitting}
                        dataTest='deposit-limits-set-button'
                    >
                        <I18n
                            langKey='account.deposit-limits-edit.submit.label'
                            defaultText='Set limit'
                        />
                    </ButtonSubmit>
                    <FullWidthSecondaryButton
                        type='button'
                        size='medium'
                        limitVariant={variant}
                        onClick={onCancel}
                        dataTest='deposit-limits-cancel-button'
                    >
                        <I18n
                            langKey='account.deposit-limits-edit.cancel.label'
                            defaultText='Cancel'
                        />
                    </FullWidthSecondaryButton>
                </EditFormActions>
            )}
        </RollingDepositLimitForm>
    );
});

const depositLimitsSectionTitle = (calculationType: getRollingLimits.CalculationTypeSchemaType): JSX.Element => {
    if (calculationType === 'gross') {
        return (
            <I18n
                langKey='account.deposit-limit-readonly.title'
                defaultText='Deposit Limits'
            />
        );
    }

    return (
        <I18n
            langKey='account.deposit-limit-readonly.net-title'
            defaultText='Net Deposit Limits'
        />
    );
};

export const DepositLimitsReadonlyCards = observer(
    'DepositLimitsReadonlyCards',
    ({
        state,
        calculationType,
        isDarkMode,
    }: {
        state: RollingNetDepositLimitState;
        calculationType: getRollingLimits.CalculationTypeSchemaType;
        isDarkMode?: boolean;
    }): JSX.Element => {
        const items = state.limitsFor(calculationType);

        return (
            <ReadonlyCardsStack>
                <DepositLimitReadonlyCard
                    state={items.daily}
                    depositLimitTime='daily'
                    isDarkMode={isDarkMode}
                    calculationType={calculationType}
                />
                <DepositLimitReadonlyCard
                    state={items.weekly}
                    depositLimitTime='weekly'
                    isDarkMode={isDarkMode}
                    calculationType={calculationType}
                />
                <DepositLimitReadonlyCard
                    state={items.monthly}
                    depositLimitTime='monthly'
                    isDarkMode={isDarkMode}
                    calculationType={calculationType}
                />
            </ReadonlyCardsStack>
        );
    }
);

const DepositLimitsReadonlySection = observer(
    'DepositLimitsReadonlySection',
    ({
        state,
        calculationType,
    }: {
        state: RollingNetDepositLimitState;
        calculationType: getRollingLimits.CalculationTypeSchemaType;
    }): JSX.Element => {
        const common = useCommon();
        const usersState = UsersState.get(common);
        const starRouter = StarRouter.get(common);

        return (
            <LimitsTypeSection data-test={`deposit-limits-section-${calculationType}`}>
                <HeaderTitle data-test={`deposit-limits-main-title-${calculationType}`}>
                    {depositLimitsSectionTitle(calculationType)}
                </HeaderTitle>
                <ReadonlyIntro data-test={`deposit-limits-readonly-subtitle-${calculationType}`}>
                    <I18n
                        langKey='account.deposit-limit-readonly.subtitle'
                        defaultText='Set individual limits for each time period.'
                    />
                </ReadonlyIntro>
                <DepositLimitsReadonlyCards
                    state={state}
                    calculationType={calculationType}
                />
                <ButtonSubmit
                    type='button'
                    size='medium'
                    onClick={(): void => {
                        usersState.setDepositLimitsEditCalculationType(calculationType);
                        starRouter.redirectToLimitsDepositEdit();
                    }}
                    dataTest={`deposit-limits-edit-button-${calculationType}`}
                >
                    <I18n
                        langKey='account.deposit-limit-readonly.edit'
                        defaultText='Edit'
                    />
                </ButtonSubmit>
            </LimitsTypeSection>
        );
    }
);

export const DepositLimitsProcedure = observer('DepositLimitsProcedure', () => {
    const common = useCommon();
    const configComponents = ConfigComponents.get(common);
    const usersState = UsersState.get(common);
    const languagesState = LanguagesState.get(common);
    const depositLimitsStackedView = configComponents.config.depositLimitsStackedView;

    const [state] = useState(
        () => new RollingNetDepositLimitState(common, configComponents, usersState, languagesState, () => {})
    );

    return (
        <div data-test='rolling-net-deposit-limits-procedure'>
            <SectionDescritpion data-test='deposit-limits-general-description'>
                <I18n
                    langKey='account.deposit-limit.description.intro'
                    defaultText='Manage your account safety by setting spending boundaries. You can choose between '
                />
                <InlineTermWithTooltip>
                    <CopyTerm>
                        <I18n
                            langKey='account.deposit-limit.description.gross-label'
                            defaultText='Deposit Limits'
                        />
                    </CopyTerm>
                    <Tooltip
                        portal={true}
                        type='info'
                        dataTest='deposit-limits-general-gross-tooltip'
                        content={
                            <I18n
                                langKey='account.deposit-limit.description.gross-tooltip'
                                defaultText='Deposit Limits track your total deposits made over a set period, ignoring withdrawals.'
                            />
                        }
                    />
                </InlineTermWithTooltip>
                <I18n
                    langKey='account.deposit-limit.description.or'
                    defaultText=' or '
                />
                <InlineTermWithTooltip>
                    <CopyTerm>
                        <I18n
                            langKey='account.deposit-limit.description.net-label'
                            defaultText='Net Deposit Limits'
                        />
                    </CopyTerm>
                    <Tooltip
                        portal={true}
                        type='info'
                        dataTest='deposit-limits-general-net-tooltip'
                        content={
                            <I18n
                                langKey='account.deposit-limit.description.net-tooltip'
                                defaultText='Net Deposit Limits calculate your total deposits minus any withdrawals.'
                            />
                        }
                    />
                </InlineTermWithTooltip>
                <I18n
                    langKey='account.deposit-limit.description.outro'
                    defaultText='. Choose the method that best helps you stay in control of your budget.'
                />
            </SectionDescritpion>
            {state.rollingNetDepositLimitStatus === 'loading' ? (
                <I18n
                    langKey='account.contact-preferences.loading'
                    defaultText='Loading...'
                />
            ) : state.rollingNetDepositLimitStatus === 'error' ? (
                renderLimitApiMessage('error')
            ) : (
                <>
                    {depositLimitsStackedView ? null : (
                        <DepositLimitsCalculationToggle
                            value={state.calculationType ?? 'gross'}
                            onChange={state.selectCalculationType}
                        />
                    )}
                    {depositLimitsStackedView ? (
                        <>
                            <DepositLimitsReadonlySection
                                state={state}
                                calculationType='gross'
                            />
                            <DepositLimitsReadonlySection
                                state={state}
                                calculationType='net'
                            />
                        </>
                    ) : (
                        <DepositLimitsReadonlySection
                            state={state}
                            calculationType={state.calculationType ?? 'gross'}
                        />
                    )}
                    {renderLimitApiMessage(state.infoMessage)}
                </>
            )}
        </div>
    );
});

export { TopUpLimitsProcedureItem, ButtonSubmit };
